import React, { ReactNode, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { TelemetryProvider } from './TelemetryContext'
import type { AddressData, BaseAgentData, Configuration, CustomVoice, MapConfiguration, PonyfillFactoryCredentials, VeilleConfiguration, ViewsConfiguration } from '../../models/types'
import { checkLocale } from '../../utils/checkLocale'

interface SpeechProviderProps {
  ponyfillFactoryCredentials: PonyfillFactoryCredentials
  customVoice: CustomVoice | undefined
}

interface ViewsProviderProps {
  viewsConfiguration: ViewsConfiguration
  veilleConfiguration: VeilleConfiguration
  mapConfiguration: MapConfiguration
}

interface ContextProviderProps extends SpeechProviderProps, ViewsProviderProps {
  skipLoader?: boolean
  mode: number
  configuration: Configuration
  addressData: AddressData
  agentSource: BaseAgentData
  children: ReactNode
}

interface LocationData {
  searchForLocation: boolean
  latitude: number
  longitude: number
  city?: string
  country?: string
}

const ContextProvider = ({
  skipLoader,
  mode,
  viewsConfiguration,
  veilleConfiguration,
  mapConfiguration,
  configuration,
  customVoice,
  addressData,
  agentSource,
  ponyfillFactoryCredentials,
  children
}: ContextProviderProps): JSX.Element | null => {
  const dispatch = useDispatch()
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const supported = useSelector((state: RootState) => state.localeReducer.supported)
  const defaultLocale = useSelector((state: RootState) => state.localeReducer.defaultLocale)
  const currentDeviceType = useSelector((state: RootState) => state.viewReducer.currentDeviceType)

  const setLocation = async (data): Promise<void> => {
    const position: LocationData = {
      searchForLocation: true,
      latitude: data.coords.latitude,
      longitude: data.coords.longitude
    }

    dispatch(
      storeActions.directline.setPositionToSend({
        latitude: position.latitude,
        longitude: position.longitude
      })
    )

    dispatch(
      storeActions.retorik.setConfiguration({
        ...configuration,
        position: position
      })
    )
  }

  useEffect(() => {
    // Set continous recognition state
    dispatch(storeActions.speech.setUseContinuousRecognition({ deviceType: currentDeviceType, options: configuration?.speechRecognitionOptions }))
  }, [currentDeviceType])

  useEffect(() => {
    dispatch(storeActions.speech.setMode(mode))
  }, [mode])

  useEffect(() => {
    dispatch(storeActions.retorik.setSkipLoader(!!skipLoader))
  }, [skipLoader])

  useEffect(() => {
    dispatch(storeActions.view.setViewsConfiguration(viewsConfiguration))
  }, [viewsConfiguration, ...Object.values(viewsConfiguration)])

  useEffect(() => {
    dispatch(storeActions.view.setVeilleConfiguration(veilleConfiguration))
  }, [veilleConfiguration, ...Object.values(veilleConfiguration)])

  useEffect(() => {
    dispatch(storeActions.view.setMapConfiguration(mapConfiguration))
  }, [mapConfiguration, ...Object.values(mapConfiguration)])

  useEffect(() => {
    dispatch(storeActions.view.setVeilleConfiguration(veilleConfiguration))
  }, [veilleConfiguration, ...Object.values(veilleConfiguration)])

  useEffect(() => {
    dispatch(storeActions.retorik.setConfiguration(configuration))
  }, [configuration, ...Object.values(configuration)])

  useEffect(() => {
    if (configuration.position?.searchForLocation) {
      // Position enabled
      if (configuration.position.latitude === undefined && configuration.position.longitude === undefined) {
        // Try to get the position of the device if no one has been given in the configuration
        navigator.geolocation?.getCurrentPosition(
          setLocation,
          (error) => {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                console.warn('Geolocation : User denied the request for Geolocation.')
                break
              case error.POSITION_UNAVAILABLE:
                console.warn('Geolocation : Location information is unavailable.')
                break
              case error.TIMEOUT:
                console.warn('Geolocation : The request to get user location timed out.')
                break
              default:
                console.warn('Geolocation : An unknown error occurred.')
                break
            }
          },
          {
            timeout: 3000
          }
        )
      }
    }
  }, [configuration.position])

  useEffect(() => {
    configuration.position?.latitude &&
      configuration.position.longitude &&
      dispatch(
        storeActions.directline.setPositionToSend({
          latitude: configuration.position.latitude,
          longitude: configuration.position.longitude
        })
      )
  }, [configuration.position?.latitude, configuration.position?.longitude])

  useEffect(() => {
    if (configuration.disableSound === true) {
      dispatch(storeActions.speech.setMuted(true))
      dispatch(storeActions.speech.setDisableSound(true))
    }
    if (configuration.disableSound === false) {
      dispatch(storeActions.speech.setDisableSound(false))
    }
  }, [configuration.disableSound])

  useEffect(() => {
    dispatch(storeActions.retorik.setSubtitlesInConfiguration(!!configuration.subtitles))
  }, [configuration.subtitles])

  useEffect(() => {
    dispatch(storeActions.speech.setCustomVoice(customVoice))
  }, [customVoice])

  useEffect(() => {
    dispatch(storeActions.retorik.setAddressData(addressData))
  }, [addressData])

  useEffect(() => {
    dispatch(storeActions.retorik.setAgentData(agentSource))
  }, [agentSource])

  useEffect(() => {
    dispatch(storeActions.speech.setPonyfillFactoryCredentials(ponyfillFactoryCredentials))
  }, [ponyfillFactoryCredentials])

  useEffect(() => {
    if (supported.length) {
      dispatch(storeActions.locale.setLocale(checkLocale({ all: supported, default: defaultLocale }, configuration.locales)))
    }
  }, [supported, configuration])

  return locale && supported ? <React.Fragment>{children}</React.Fragment> : <React.Fragment />
}

export default ContextProvider
